import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Zap, Plus, Edit2, Trash2, Play, Pause, Users, Mail, Clock, ChevronRight,
  Loader2, X, GripVertical, AlertTriangle, CheckCircle2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PIPELINE_STAGES = [
  'New Lead', 'Contacted', 'Qualified', 'Proposal Sent', 'Negotiating'
];

const EmailSequences = () => {
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingSequence, setEditingSequence] = useState(null);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [sequenceDetails, setSequenceDetails] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processResult, setProcessResult] = useState(null);

  const fetchSequences = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/sequences`, { withCredentials: true });
      setSequences(data.sequences || []);
    } catch (err) {
      console.error('Failed to fetch sequences:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchSequences();
  }, [fetchSequences]);

  const fetchSequenceDetails = async (sequenceId) => {
    try {
      const { data } = await axios.get(`${API}/api/admin/sequences/${sequenceId}`, { withCredentials: true });
      setSequenceDetails(data);
    } catch (err) {
      console.error('Failed to fetch sequence details:', err);
    }
  };

  const deleteSequence = async (sequenceId) => {
    if (!window.confirm('Delete this sequence? All active enrollments will be cancelled.')) return;
    try {
      await axios.delete(`${API}/api/admin/sequences/${sequenceId}`, { withCredentials: true });
      fetchSequences();
      if (selectedSequence === sequenceId) {
        setSelectedSequence(null);
        setSequenceDetails(null);
      }
    } catch (err) {
      console.error('Failed to delete sequence:', err);
    }
  };

  const toggleSequenceActive = async (sequence) => {
    try {
      await axios.put(`${API}/api/admin/sequences/${sequence.id}`, {
        is_active: !sequence.is_active
      }, { withCredentials: true });
      fetchSequences();
    } catch (err) {
      console.error('Failed to toggle sequence:', err);
    }
  };

  const unenrollDeal = async (enrollmentId) => {
    if (!window.confirm('Remove this deal from the sequence?')) return;
    try {
      await axios.delete(`${API}/api/admin/sequences/enrollments/${enrollmentId}`, { withCredentials: true });
      if (selectedSequence) fetchSequenceDetails(selectedSequence);
    } catch (err) {
      console.error('Failed to unenroll:', err);
    }
  };

  const runProcessor = async () => {
    setProcessing(true);
    setProcessResult(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/sequences/process`, {}, { withCredentials: true });
      setProcessResult(data);
      fetchSequences();
    } catch (err) {
      console.error('Failed to process sequences:', err);
      setProcessResult({ error: err.response?.data?.detail || 'Processing failed' });
    }
    setProcessing(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ padding: '3rem' }}>
        <Loader2 size={32} className="animate-spin" style={{ color: 'var(--teal)' }} />
      </div>
    );
  }

  return (
    <div data-testid="email-sequences">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
        <div>
          <h2 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 700, color: 'var(--navy)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Zap size={22} style={{ color: '#F59E0B' }} />
            Email Sequences
          </h2>
          <p style={{ fontSize: '0.813rem', color: '#9CA3AF', margin: 0 }}>
            Automated follow-up email campaigns
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button 
            variant="outline" 
            onClick={runProcessor} 
            disabled={processing}
            data-testid="process-sequences-btn"
          >
            {processing ? <Loader2 size={14} className="animate-spin mr-1" /> : <Play size={14} className="mr-1" />}
            Send Due Emails
          </Button>
          <Button onClick={() => { setEditingSequence(null); setShowBuilder(true); }} className="btn-primary" data-testid="create-sequence-btn">
            <Plus size={16} className="mr-1" /> New Sequence
          </Button>
        </div>
      </div>

      {/* Process Result */}
      {processResult && (
        <div style={{
          padding: '0.75rem 1rem', borderRadius: 8, marginBottom: '1rem',
          background: processResult.error ? '#FEF2F2' : '#ECFDF5',
          border: `1px solid ${processResult.error ? '#FECACA' : '#A7F3D0'}`,
          display: 'flex', alignItems: 'center', gap: '0.5rem'
        }}>
          {processResult.error ? (
            <AlertTriangle size={16} style={{ color: '#DC2626' }} />
          ) : (
            <CheckCircle2 size={16} style={{ color: '#059669' }} />
          )}
          <span style={{ fontSize: '0.813rem', color: processResult.error ? '#991B1B' : '#065F46' }}>
            {processResult.error || `Processed ${processResult.processed} emails`}
            {processResult.errors?.length > 0 && ` (${processResult.errors.length} errors)`}
          </span>
          <button onClick={() => setProcessResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={14} />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div style={{ display: 'flex', gap: '1rem' }}>
        {/* Sequences List */}
        <div style={{ flex: '0 0 320px' }}>
          {sequences.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '2rem', background: '#FAFBFC',
              borderRadius: 8, border: '1px dashed #E5E7EB'
            }}>
              <Zap size={32} style={{ color: '#D1D5DB', marginBottom: '0.5rem' }} />
              <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
                No sequences yet. Create your first automated email sequence!
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {sequences.map(seq => (
                <div
                  key={seq.id}
                  onClick={() => { setSelectedSequence(seq.id); fetchSequenceDetails(seq.id); }}
                  data-testid={`sequence-card-${seq.id}`}
                  style={{
                    padding: '1rem', background: selectedSequence === seq.id ? '#F0F9FF' : '#fff',
                    borderRadius: 8, border: selectedSequence === seq.id ? '2px solid #3B82F6' : '1px solid #E5E7EB',
                    cursor: 'pointer', transition: 'all 0.15s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--navy)', marginBottom: '0.125rem' }}>
                        {seq.name}
                      </div>
                      {seq.description && (
                        <div style={{ fontSize: '0.7rem', color: '#6B7280' }}>{seq.description}</div>
                      )}
                    </div>
                    <span style={{
                      fontSize: '0.6rem', fontWeight: 600, padding: '2px 6px', borderRadius: 100,
                      background: seq.is_active ? '#ECFDF5' : '#FEF2F2',
                      color: seq.is_active ? '#059669' : '#DC2626'
                    }}>
                      {seq.is_active ? 'Active' : 'Paused'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', fontSize: '0.7rem', color: '#9CA3AF' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Mail size={12} /> {seq.steps?.length || 0} emails
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                      <Users size={12} /> {seq.active_enrollments || 0} active
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Sequence Details */}
        <div style={{ flex: 1 }}>
          {selectedSequence && sequenceDetails ? (
            <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB', padding: '1.25rem' }}>
              {/* Detail Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <div>
                  <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
                    {sequenceDetails.sequence?.name}
                  </h3>
                  {sequenceDetails.sequence?.trigger_stage && (
                    <p style={{ fontSize: '0.75rem', color: '#6B7280', margin: '0.25rem 0 0' }}>
                      Auto-enrolls when deal enters: <strong>{sequenceDetails.sequence.trigger_stage}</strong>
                    </p>
                  )}
                </div>
                <div style={{ display: 'flex', gap: '0.375rem' }}>
                  <button
                    onClick={() => toggleSequenceActive(sequenceDetails.sequence)}
                    style={{
                      padding: '0.375rem 0.625rem', borderRadius: 4, fontSize: '0.7rem', fontWeight: 500,
                      border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: '0.25rem'
                    }}
                  >
                    {sequenceDetails.sequence?.is_active ? <Pause size={12} /> : <Play size={12} />}
                    {sequenceDetails.sequence?.is_active ? 'Pause' : 'Activate'}
                  </button>
                  <button
                    onClick={() => { setEditingSequence(sequenceDetails.sequence); setShowBuilder(true); }}
                    style={{
                      padding: '0.375rem 0.625rem', borderRadius: 4, fontSize: '0.7rem',
                      border: '1px solid #E5E7EB', background: '#fff', cursor: 'pointer'
                    }}
                  >
                    <Edit2 size={12} />
                  </button>
                  <button
                    onClick={() => deleteSequence(selectedSequence)}
                    style={{
                      padding: '0.375rem 0.625rem', borderRadius: 4, fontSize: '0.7rem',
                      border: '1px solid #FEE2E2', background: '#FEF2F2', cursor: 'pointer', color: '#DC2626'
                    }}
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              {/* Steps */}
              <div style={{ marginBottom: '1.25rem' }}>
                <h4 style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                  Email Steps
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {(sequenceDetails.sequence?.steps || []).sort((a, b) => a.order - b.order).map((step, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem',
                      background: '#FAFBFC', borderRadius: 6, border: '1px solid #E5E7EB'
                    }}>
                      <div style={{
                        width: 28, height: 28, borderRadius: '50%', background: '#E5E7EB',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 600, color: '#6B7280'
                      }}>
                        {i + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 500, fontSize: '0.813rem', color: 'var(--navy)' }}>
                          {step.subject}
                        </div>
                        <div style={{ fontSize: '0.7rem', color: '#9CA3AF', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <Clock size={10} />
                          {i === 0 ? `Send after ${step.delay_days} day(s)` : `Wait ${step.delay_days} day(s) after previous`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Enrollments */}
              <div>
                <h4 style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                  Enrolled Deals ({sequenceDetails.enrollments?.length || 0})
                </h4>
                {(sequenceDetails.enrollments || []).length === 0 ? (
                  <p style={{ fontSize: '0.813rem', color: '#9CA3AF', padding: '1rem', textAlign: 'center' }}>
                    No deals enrolled yet
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                    {sequenceDetails.enrollments.map(enrollment => (
                      <div key={enrollment.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0.625rem 0.75rem', background: '#fff', borderRadius: 6, border: '1px solid #E5E7EB'
                      }}>
                        <div>
                          <div style={{ fontWeight: 500, fontSize: '0.813rem', color: 'var(--navy)' }}>
                            {enrollment.deal_name}
                          </div>
                          <div style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>
                            Step {enrollment.current_step + 1} of {enrollment.total_steps} • 
                            {enrollment.emails_sent} sent • 
                            Next: {formatDate(enrollment.next_send_at)}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{
                            fontSize: '0.6rem', fontWeight: 600, padding: '2px 6px', borderRadius: 100,
                            background: enrollment.status === 'active' ? '#ECFDF5' : (enrollment.status === 'completed' ? '#EFF6FF' : '#F3F4F6'),
                            color: enrollment.status === 'active' ? '#059669' : (enrollment.status === 'completed' ? '#3B82F6' : '#6B7280'),
                            textTransform: 'capitalize'
                          }}>
                            {enrollment.status}
                          </span>
                          {enrollment.status === 'active' && (
                            <button
                              onClick={(e) => { e.stopPropagation(); unenrollDeal(enrollment.id); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}
                            >
                              <X size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: 300, background: '#FAFBFC', borderRadius: 8, border: '1px dashed #E5E7EB'
            }}>
              <p style={{ fontSize: '0.875rem', color: '#9CA3AF' }}>
                Select a sequence to view details
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Sequence Builder Modal */}
      {showBuilder && (
        <SequenceBuilder
          sequence={editingSequence}
          onClose={() => { setShowBuilder(false); setEditingSequence(null); }}
          onSave={() => { setShowBuilder(false); setEditingSequence(null); fetchSequences(); }}
        />
      )}
    </div>
  );
};


// ==================== SEQUENCE BUILDER MODAL ====================
const SequenceBuilder = ({ sequence, onClose, onSave }) => {
  const [name, setName] = useState(sequence?.name || '');
  const [description, setDescription] = useState(sequence?.description || '');
  const [triggerStage, setTriggerStage] = useState(sequence?.trigger_stage || '');
  const [steps, setSteps] = useState(sequence?.steps || [{ order: 0, delay_days: 1, subject: '', body: '', is_html: false }]);
  const [saving, setSaving] = useState(false);

  const addStep = () => {
    setSteps([...steps, {
      order: steps.length,
      delay_days: 2,
      subject: '',
      body: '',
      is_html: false
    }]);
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index] = { ...newSteps[index], [field]: value };
    setSteps(newSteps);
  };

  const removeStep = (index) => {
    if (steps.length <= 1) return;
    const newSteps = steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i }));
    setSteps(newSteps);
  };

  const saveSequence = async () => {
    if (!name.trim() || steps.some(s => !s.subject.trim() || !s.body.trim())) {
      alert('Please fill in all required fields');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        name,
        description,
        trigger_stage: triggerStage || null,
        steps: steps.map((s, i) => ({ ...s, order: i }))
      };
      
      if (sequence?.id) {
        await axios.put(`${API}/api/admin/sequences/${sequence.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/api/admin/sequences`, payload, { withCredentials: true });
      }
      onSave();
    } catch (err) {
      console.error('Failed to save sequence:', err);
      alert(err.response?.data?.detail || 'Failed to save sequence');
    }
    setSaving(false);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
    }}>
      <div style={{
        background: '#fff', borderRadius: 12, width: 700, maxWidth: '100%', maxHeight: '90vh',
        display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{ padding: '1.25rem', borderBottom: '1px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 700, color: 'var(--navy)', margin: 0 }}>
            {sequence ? 'Edit Sequence' : 'Create Email Sequence'}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem' }}>
          {/* Basic Info */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                Sequence Name *
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., New Lead Follow-up"
                data-testid="sequence-name-input"
                style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                  Description
                </label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Brief description"
                  style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.7rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>
                  Auto-Enroll Trigger (Optional)
                </label>
                <select
                  value={triggerStage}
                  onChange={(e) => setTriggerStage(e.target.value)}
                  style={{
                    width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #E5E7EB',
                    fontSize: '0.875rem', color: 'var(--navy)', background: '#fff', height: 40
                  }}
                >
                  <option value="">Manual enrollment only</option>
                  {PIPELINE_STAGES.map(stage => (
                    <option key={stage} value={stage}>When deal enters: {stage}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Email Steps */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
              <h4 style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--navy)', margin: 0 }}>
                Email Steps ({steps.length})
              </h4>
              <button
                onClick={addStep}
                style={{
                  display: 'flex', alignItems: 'center', gap: '0.25rem', padding: '0.375rem 0.625rem',
                  borderRadius: 4, fontSize: '0.7rem', fontWeight: 500, border: '1px solid #E5E7EB',
                  background: '#fff', cursor: 'pointer', color: '#6B7280'
                }}
              >
                <Plus size={12} /> Add Step
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {steps.map((step, i) => (
                <div key={i} style={{
                  padding: '1rem', background: '#FAFBFC', borderRadius: 8, border: '1px solid #E5E7EB'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', background: 'var(--teal)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.7rem', fontWeight: 600, color: '#fff'
                      }}>
                        {i + 1}
                      </div>
                      <span style={{ fontSize: '0.813rem', fontWeight: 600, color: 'var(--navy)' }}>
                        Email {i + 1}
                      </span>
                    </div>
                    {steps.length > 1 && (
                      <button onClick={() => removeStep(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626' }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '100px 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <div>
                      <label style={{ fontSize: '0.6rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                        Delay (days)
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={step.delay_days}
                        onChange={(e) => updateStep(i, 'delay_days', parseInt(e.target.value) || 0)}
                        style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
                      />
                    </div>
                    <div>
                      <label style={{ fontSize: '0.6rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                        Subject *
                      </label>
                      <Input
                        value={step.subject}
                        onChange={(e) => updateStep(i, 'subject', e.target.value)}
                        placeholder="Email subject line"
                        data-testid={`step-${i}-subject`}
                        style={{ background: '#fff', border: '1px solid #E5E7EB', color: 'var(--navy)' }}
                      />
                    </div>
                  </div>

                  <div>
                    <label style={{ fontSize: '0.6rem', fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', display: 'block', marginBottom: 3 }}>
                      Body * <span style={{ fontWeight: 400, textTransform: 'none' }}>(Use {'{{name}}'} and {'{{company}}'} for personalization)</span>
                    </label>
                    <textarea
                      value={step.body}
                      onChange={(e) => updateStep(i, 'body', e.target.value)}
                      placeholder="Email body content..."
                      rows={4}
                      data-testid={`step-${i}-body`}
                      style={{
                        width: '100%', padding: '0.5rem', borderRadius: 6, border: '1px solid #E5E7EB',
                        fontSize: '0.813rem', color: 'var(--navy)', resize: 'vertical', fontFamily: 'var(--font-body)'
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem 1.25rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={saveSequence} disabled={saving} className="btn-primary" data-testid="save-sequence-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : null}
            {sequence ? 'Save Changes' : 'Create Sequence'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailSequences;
